import type {GetServerSideProps, GetServerSidePropsResult} from 'next';
import * as z from 'zod';
import {routes} from '../../config/constants';
import {QUERY_PARAMS_FOR_REDIRECT_FROM_LOGIN_PAGE} from '../../core/entities/login/constants';
import {validateCookie} from '../../core/usecases/auth/checkOauthTokenExistence';
import {LoginPage} from '../../features/login/components/LoginPage';
import {withHandler} from '../../server/token/withHandler';
import {ScriptForGA} from '../../shared/components/ScriptForGA';
import {NoLeftSidebarLayout} from '../../shared/components/layouts/NoLeftSidebarLayout';
import type {NextPageWithLayout} from '../_app';

type Props = {
  nextRedirectUrl?: string;
};

const QueryParameter = z.object({
  next_redirect_url: z.string().optional(),
});
type QueryParameter = z.infer<typeof QueryParameter>;

export const getServerSideProps: GetServerSideProps = withHandler<
  Props,
  'withOptionalSession' | 'withQuery',
  QueryParameter
>(
  {auth: 'optional', queryValidator: QueryParameter},
  async (context): Promise<GetServerSidePropsResult<Props>> => {
    const {isValid} = validateCookie(context.req.cookies);
    if (isValid) {
      return {
        redirect: {
          permanent: false,
          destination: routes.HOME,
        },
      };
    }

    let nextRedirectUrl = context.validatedQuery.next_redirect_url;
    const {query} = context;
    if (nextRedirectUrl && Object.keys(query).length > 1) {
      for (const key of Object.keys(query)) {
        if (key !== QUERY_PARAMS_FOR_REDIRECT_FROM_LOGIN_PAGE) {
          const queryValue = Array.isArray(query[key])
            ? query[key].join(',')
            : query[key];
          nextRedirectUrl = `${nextRedirectUrl}&${key}=${queryValue}`;
        }
      }
    }

    return {
      props: {
        nextRedirectUrl: nextRedirectUrl ? nextRedirectUrl : '',
      },
    };
  },
);

const Page: NextPageWithLayout<Props> = ({nextRedirectUrl}: Props) => {
  return (
    <div className="mt-16 w-full flex justify-center">
      <LoginPage nextRedirectUrl={nextRedirectUrl} />
      <ScriptForGA userDataForAnalytics={undefined} />
    </div>
  );
};

export default Page;

Page.getLayout = (page) => <NoLeftSidebarLayout>{page}</NoLeftSidebarLayout>;
